// src/api/swarm.ts
import { API_BASE_URL, getHeaders, clearAuth } from './client';

export type SwarmTaskStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
export type SwarmAgentRowStatus = 'idle' | 'working' | 'done' | 'error';

export interface CreateSwarmResponse {
  id: string;
  status: SwarmTaskStatus;
  message: string;
  progress: number;
  agentCount: number;
}

export interface SwarmStatusAgentRow {
  agent_id: string;
  agent_name: string;
  role: string;
  status: SwarmAgentRowStatus;
  output?: string;
  depends_on?: string[];
  [key: string]: any;
}

export interface SwarmStatusResponse {
  id: string;
  status: SwarmTaskStatus;
  progress: number;
  agents: SwarmStatusAgentRow[];
  logCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SwarmResultAgent {
  name: string;
  status: SwarmAgentRowStatus;
  output: string;
}

export interface SwarmResultResponse {
  id: string;
  status: SwarmTaskStatus;
  result: string | null;
  agents: SwarmResultAgent[];
  completedAt: string | null;
  message: string;
}

export interface CancelSwarmResponse {
  id: string;
  status: 'cancelled';
  message: string;
}

async function handleResponse<T>(res: Response, action: string): Promise<T> {
  if (res.status === 401) {
    clearAuth();
    throw new Error('Session expired. Please log in again.');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${action} failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function createSwarm(task: string, agentCount?: number): Promise<CreateSwarmResponse> {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      task,
      ...(agentCount ? { options: { agent_count: agentCount } } : {}),
    }),
  });
  return handleResponse(res, 'Create swarm');
}

export async function executeSwarm(taskId: string): Promise<{ id: string; status: string; message: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/execute`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res, 'Execute swarm');
}

export async function getSwarmStatus(taskId: string): Promise<SwarmStatusResponse> {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/status`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(res, 'Get swarm status');
}

export async function getSwarmResult(taskId: string): Promise<SwarmResultResponse> {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/result`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(res, 'Get swarm result');
}

export async function cancelSwarm(taskId: string): Promise<CancelSwarmResponse> {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res, 'Cancel swarm');
}
